import { AsyncHelper, ResourceModel } from '@bryntum/schedulerpro';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useMaskRenderer } from 'components/organisms/resource-management/scheduler-config/mask-renderer';
import { useNoProjectsRender } from 'components/organisms/resource-management/scheduler-config/no-projects-renderer';
import { isNonWorkingDay } from 'components/organisms/timesheets/common/utils';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { SchedulerReference } from '../types';

interface Props {
  schedulerRef: SchedulerReference;
  workingDaysConfig: WorkingDaysConfiguration;
  projectInfoProvider: () => any;
}
interface ResultValue {
  isResourceStoreLoaded: boolean;
}

export const useResourcesLoader = ({
  schedulerRef,
  workingDaysConfig,
  projectInfoProvider,
}: Props): ResultValue => {
  const [isResourceStoreLoaded, setIsResourceStoreLoaded] = useState(false);
  const [isSchedulerInitializing, setSchedulerInitializing] = useState(false);

  const { showMask, hideMask } = useMaskRenderer({ schedulerRef });

  const { showNoProjects } = useNoProjectsRender({ schedulerRef });

  const loadSchedulerDefaults = useCallback(async () => {
    showMask();
    await AsyncHelper.sleep(100);
    schedulerRef.current?.instance.suspendRefresh();

    const project = projectInfoProvider();

    if (project.resourceStore?.data.length === 0) {
      hideMask();
      showNoProjects();
    }

    if (schedulerRef.current) {
      // eslint-disable-next-line no-param-reassign
      schedulerRef.current.instance.project = project;
    }

    (project.resourceStore?.data as ResourceModel[]).forEach(({ id }) => {
      schedulerRef.current?.instance.collapse(id);
    });

    schedulerRef.current?.instance.timeAxis.filter({
      filters: {
        id: 'non-working-day',
        filterBy: (t) => {
          return !isNonWorkingDay(moment(t.startDate), workingDaysConfig);
        },
      },
      reapplyFilterOnAdd: true,
      reapplyFilterOnUpdate: true,
    });

    schedulerRef.current?.instance.scrollToNow({
      block: 'center',
    });
    schedulerRef.current?.instance.resumeRefresh(true);
    setIsResourceStoreLoaded(true);
    hideMask();
  }, [
    hideMask,
    projectInfoProvider,
    schedulerRef,
    showMask,
    showNoProjects,
    workingDaysConfig,
  ]);

  useEffect(() => {
    if (isSchedulerInitializing) {
      return;
    }
    setSchedulerInitializing(true);
    loadSchedulerDefaults();
  }, [loadSchedulerDefaults, isSchedulerInitializing]);

  return {
    isResourceStoreLoaded,
  };
};
